import { css } from '@emotion/react';
import { FC, useEffect } from 'react';
import { colors, fontStyles, spacing, safeAreaInset } from '../../styles';
import closeIcon from '../../assets/close.svg';

export interface BottomSheetProps {
  isOpen: boolean;
  title?: string;
  onClose: () => void;
  onOpen?: () => void;
  children: React.ReactNode;
  headerStyle?: 'centered' | 'left-aligned';
}

// Extremely simplified bottom sheet with minimal dependencies
const BottomSheet: FC<BottomSheetProps> = ({ isOpen, title, onClose, onOpen, children, headerStyle = 'centered' }) => {
  // Run side effects only when sheet opens or closes
  useEffect(() => {
    if (isOpen) {
      // Disable body scrolling when open
      document.body.style.overflow = 'hidden';

      // Call onOpen if provided
      if (onOpen) onOpen();
    }

    // Clean up when component unmounts or isOpen changes
    return () => {
      // Re-enable body scrolling
      document.body.style.overflow = '';
    };
  }, [isOpen, onOpen]);

  // Don't render anything if not open
  if (!isOpen) return null;

  return (
    <div className="bottom-sheet-overlay" css={overlayStyles} onClick={onClose}>
      <div className="bottom-sheet-container" css={containerStyles} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div css={headerStyle === 'left-aligned' ? leftAlignedHeaderStyles : headerStyles}>
            <h2 css={headerStyle === 'left-aligned' ? leftAlignedTitleStyles : titleStyles}>{title}</h2>
            <button
              css={headerStyle === 'left-aligned' ? leftAlignedCloseButtonStyles : closeButtonStyles}
              onClick={onClose}
              aria-label="Close"
            >
              {headerStyle === 'left-aligned' ? (
                <span
                  css={css`
                    font-size: 24px;
                    line-height: 1;
                  `}
                >
                  ×
                </span>
              ) : (
                <img src={closeIcon} alt="Close" width="16" height="16" />
              )}
            </button>
          </div>
        )}
        <div css={contentStyles}>{children}</div>
      </div>
    </div>
  );
};

// Simple styles without complex animations
const overlayStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
`;

const containerStyles = css`
  background-color: var(--background-color);
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-height: 80vh;
  position: relative;
  box-shadow: 0px -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1001; /* Ensure bottom sheet content is above overlay */
  display: flex;
  flex-direction: column;

  html[data-theme='dark'] & {
    background-color: ${colors.liquorice};
    border-top: 1px solid ${colors.tungsten}40;
  }
`;

const headerStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.centi} ${spacing.deci};
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;

  html[data-theme='dark'] & {
    border-bottom: 1px solid ${colors.tungsten}40;
  }
`;

const titleStyles = css`
  ${fontStyles.dog};
  color: var(--text-color);
  margin: ${spacing.deci} 0;
  text-align: center;
  width: 100%;
`;

const closeButtonStyles = css`
  background: none;
  border: none;
  cursor: pointer;
  position: absolute;
  right: ${spacing.centi};
  top: 50%;
  transform: translateY(-50%);
  padding: ${spacing.milli};
`;

const contentStyles = css`
  padding: ${spacing.deci};
  overflow-y: auto;
  flex: 1;
  padding-bottom: max(${safeAreaInset.bottom}, 16px);
`;

// Left-aligned header styles
const leftAlignedHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.regular} ${spacing.deci} ${spacing.centi} ${spacing.regular};
  flex-shrink: 0;
`;

const leftAlignedTitleStyles = css`
  ${fontStyles.shark};
  color: ${colors.liquorice};
  margin: 0;
  font-weight: 600;

  html[data-theme='dark'] & {
    color: ${colors.sugar};
  }
`;

const leftAlignedCloseButtonStyles = css`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${colors.tungsten};
  font-weight: 300;

  &:hover {
    color: ${colors.liquorice};
  }

  html[data-theme='dark'] & {
    color: ${colors.cloud};

    &:hover {
      color: ${colors.sugar};
    }
  }
`;

export default BottomSheet;
